/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  createEnhanceSpeechTrackExport,
  EnhanceSpeechTrackExport,
  EnhanceSpeechTrackId,
  getEnhanceSpeechTrackExport,
} from "components/EnhancedSpeech/Utilities";
import { useCallback, useMemo, useReducer } from "react";
import { useJitterInterval } from "hooks/useJitterInterval";
import { useDispatch } from "react-redux";
import { addToast } from "actions/ToastMessageActions";
import { noticeError } from "utilities/noticeError";

interface EnhanceSpeechTrackExportHook {
  exportTrack: (
    trackId: EnhanceSpeechTrackId,
    enhanceStrength: number,
    backgroundStrength: number,
    enhancementEnabled: boolean,
    modelVersion: string
  ) => void;
  exporting: EnhanceSpeechTrackId[];
}

export const useEnhanceSpeechExports = (): EnhanceSpeechTrackExportHook => {
  const [{ creatingExport, pollingExports }, dispatch] = useReducer(
    enhanceSpeechExportsReducer,
    initialEnhanceSpeechExportState
  );

  const handleError = useHandleError(dispatch);

  const exportTrack = useCallback(
    async (
      trackId: EnhanceSpeechTrackId,
      enhanceStrength: number,
      backgroundStrength: number,
      enhancementEnabled: boolean,
      modelVersion: string
    ) => {
      dispatch({
        type: "CREATE_EXPORT",
        trackId,
      });

      try {
        const trackExport = await createEnhanceSpeechTrackExport(
          trackId,
          enhanceStrength,
          backgroundStrength,
          enhancementEnabled,
          modelVersion
        );

        dispatch({
          type: "FINISH_CREATING_EXPORT",
          trackExport,
        });

        if (trackExport.url !== null) {
          download(trackExport.url);
        }
      } catch (e) {
        handleError(trackId);
        noticeError(e);
      }
    },
    [handleError]
  );

  const pollForExports = useCallback(() => {
    pollingExports.forEach(async ({ trackExport, startedAt }) => {
      try {
        const updatedTrack = await getEnhanceSpeechTrackExport(
          trackExport.enhanceSpeechTrackId,
          trackExport.id
        );

        if (updatedTrack.url !== null) {
          download(updatedTrack.url);
          dispatch({
            type: "REMOVE_EXPORT",
            trackId: trackExport.enhanceSpeechTrackId,
          });
        } else if (updatedTrack.errorType !== null) {
          handleError(trackExport.enhanceSpeechTrackId);
        } else {
          const diffMs = new Date().getTime() - startedAt.getTime();
          let fiveMinutesMs = 5 * 60 * 1000;
          if (diffMs > fiveMinutesMs) {
            noticeError("Track export timed out");
            handleError(trackExport.enhanceSpeechTrackId);
          }
        }
      } catch (e) {
        noticeError(e);

        // user can retry if this was a transient error or network connectivity.
        // the export will still be on the backend so they don't have to start from scratch.
        handleError(trackExport.enhanceSpeechTrackId);
      }
    });
  }, [handleError, pollingExports]);

  useJitterInterval(pollForExports, 2000, 500);

  const exporting = useMemo(() => {
    return creatingExport.concat(
      pollingExports.map(
        (pollingExport) => pollingExport.trackExport.enhanceSpeechTrackId
      )
    );
  }, [creatingExport, pollingExports]);

  return { exportTrack, exporting };
};

type EnhanceSpeechExportsActions =
  | {
      type: "CREATE_EXPORT";
      trackId: EnhanceSpeechTrackId;
    }
  | {
      type: "FINISH_CREATING_EXPORT";
      trackExport: EnhanceSpeechTrackExport;
    }
  | {
      type: "FAIL_CREATING_EXPORT";
      trackId: EnhanceSpeechTrackId;
    }
  | {
      type: "REMOVE_EXPORT";
      trackId: EnhanceSpeechTrackId;
    };

interface PolledExport {
  trackExport: EnhanceSpeechTrackExport;
  startedAt: Date;
}

interface EnhanceSpeechExportState {
  creatingExport: EnhanceSpeechTrackId[];
  pollingExports: PolledExport[];
}

const initialEnhanceSpeechExportState: EnhanceSpeechExportState = {
  creatingExport: [],
  pollingExports: [],
};

const enhanceSpeechExportsReducer: React.Reducer<
  EnhanceSpeechExportState,
  EnhanceSpeechExportsActions
> = (state, action) => {
  switch (action.type) {
    case "CREATE_EXPORT":
      return {
        ...state,
        creatingExport: state.creatingExport.concat(action.trackId),
      };
    case "FAIL_CREATING_EXPORT":
      return {
        ...state,
        creatingExport: state.creatingExport.filter(
          (id) => id !== action.trackId
        ),
      };
    case "FINISH_CREATING_EXPORT":
      if (
        action.trackExport.url !== null ||
        action.trackExport.errorType !== null
      ) {
        return {
          ...state,
          creatingExport: state.creatingExport.filter(
            (id) => id !== action.trackExport.enhanceSpeechTrackId
          ),
        };
      } else {
        return {
          ...state,
          pollingExports: state.pollingExports.concat({
            trackExport: action.trackExport,
            startedAt: new Date(),
          }),
          creatingExport: state.creatingExport.filter(
            (id) => id !== action.trackExport.enhanceSpeechTrackId
          ),
        };
      }
    case "REMOVE_EXPORT":
      return {
        ...state,
        creatingExport: state.creatingExport.filter(
          (id) => id !== action.trackId
        ),
        pollingExports: state.pollingExports.filter(
          (pollingExport) =>
            pollingExport.trackExport.enhanceSpeechTrackId !== action.trackId
        ),
      };
  }
};

const useHandleError = (
  localDispatch: React.Dispatch<EnhanceSpeechExportsActions>
): ((trackId: EnhanceSpeechTrackId) => void) => {
  const globalDispatch = useDispatch();
  return useCallback(
    (trackId: EnhanceSpeechTrackId): void => {
      globalDispatch(
        addToast({
          message: I18n.t("enhance_speech.export.error"),
        })
      );
      localDispatch({
        type: "REMOVE_EXPORT",
        trackId,
      });
    },
    [globalDispatch, localDispatch]
  );
};

const download = (url: string): void => {
  const downloadLink = document.createElement("a");
  downloadLink.href = url;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};
